// frontend/src/stores/sidebarStore.ts — shell layout client state.
//
// Two pieces of UI state the shell owns (CONVENTIONS §Frontend state — Zustand
// for client state, never React Context):
//   - `collapsed`     — the admin section sidebar collapsed/expanded. Persisted
//                       (a preference that should survive reloads). Replaces
//                       dev's raw `localStorage.adminSidebarCollapsed`.
//   - `mobileNavOpen` — the <768 hamburger drawer open/closed. Ephemeral (never
//                       persisted — a reload should always start closed).
//
// `persist` whitelists only `collapsed` via `partialize`, so `mobileNavOpen`
// never leaks into storage.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface SidebarState {
  /** Admin sidebar collapsed (desktop). Persisted. */
  collapsed: boolean;
  /** Mobile hamburger drawer open. Ephemeral. */
  mobileNavOpen: boolean;
  toggleCollapsed: () => void;
  setMobileNavOpen: (open: boolean) => void;
}

export const useSidebarStore = create<SidebarState>()(
  persist(
    (set) => ({
      collapsed: false,
      mobileNavOpen: false,
      toggleCollapsed: () => set((s) => ({ collapsed: !s.collapsed })),
      setMobileNavOpen: (open) => set({ mobileNavOpen: open }),
    }),
    {
      name: 'ems-sidebar',
      partialize: (s) => ({ collapsed: s.collapsed }),
    },
  ),
);
